import React, { useState } from 'react'
import styled from 'styled-components'
import { useTheme } from '../../../middleware/ThemeContext';
import { Theme } from '../../../utils/themes';
import StyledText from '../../StyledComponents/StyledText';
import { TempSettings } from '../../../types/types';
import { capitalizeFirstLetter, hexToRgba } from '../../../utils/helper';
import StyledInput from '../../StyledComponents/StyledInput';
import { StyledHorizontalLine } from '../../StyledComponents/Styledhr';
import { Radio } from '@mui/material';
import LeftAlignIcon from '../../../assets/icons/LeftAlignIcon';
import CenterAlignIcon from '../../../assets/icons/CenterAlignIcon';
import RightAlignIcon from '../../../assets/icons/RightAlignIcon';
import StyledRadio from '../../StyledComponents/StyledRadio';
import AMSSelect from '../../AMSSelect';
import ImageIcon from '../../../assets/icons/ImageIcon';
import VideoIcon from '../../../assets/icons/VideoIcon';
import { Formik } from 'formik';
import FormikImageUpload from '../../Formik/FormikImageUpload';
import { useToast } from '../../toast/ToastContext';
import { useLogoPreSignedUrlMutation } from '../../../api/pageBuilderApiSlice';
import CustomInputURL from '../../form/CustomInputURL';
import StyledCheckBox from '../../StyledComponents/CheckBox/StyledCheckBox';

interface UploadedFile {
    file: File;
    name: string;
    type: string;
    rename: string;
    progress: number;
}

const MediaProperties = ({ updateTempSetting, tempSettings, onUploadStateChange }: { updateTempSetting: (key: string, value: any) => void, tempSettings: TempSettings, onUploadStateChange?: (isUploading: boolean) => void }) => {
    const { theme } = useTheme();
    const showToast = useToast();
    const [mediaPreSignedUrl] = useLogoPreSignedUrlMutation();
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadKey, setUploadKey] = useState(0);
    const [hasUploaded, setHasUploaded] = useState<boolean>(false);

    const headingOptions = [{ label: 'Image', icon: <ImageIcon color={theme.CorporateTheme.colors.gunmetal} /> }, { label: 'Video', icon: <VideoIcon color={theme.CorporateTheme.colors.gunmetal} /> }];
    const textAlignmentOptions = [{ id: 'Left', icon: <LeftAlignIcon /> }, { id: 'Center', icon: <CenterAlignIcon /> }, { id: 'Right', icon: <RightAlignIcon /> }];

    const selectedMediaType = tempSettings.mediaType || 'Image';

    // Reset upload key when media type changes to force component remount
    React.useEffect(() => {
        setUploadKey(prev => prev + 1);
        setHasUploaded(false);
    }, [selectedMediaType]);

    // Notify parent when upload state changes
    React.useEffect(() => {
        onUploadStateChange?.(isUploading);
    }, [isUploading, onUploadStateChange]);

    // Get media data based on current media type
    const getMediaData = (field: string) => {
        const mediaTypePrefix = selectedMediaType.toLowerCase();
        return tempSettings[`${mediaTypePrefix}${field}`];
    };

    const setMediaData = (field: string, value: any) => {
        const mediaTypePrefix = selectedMediaType.toLowerCase();
        updateTempSetting(`${mediaTypePrefix}${field}`, value);
    };

    // Validate media file based on selected type
    const validateMediaFile = (file: UploadedFile): Promise<boolean> => {
        return new Promise((resolve) => {
            const fileType = file.type.toLowerCase();

            if (selectedMediaType === 'Image' || selectedMediaType === 'image') {
                // Allow only image files
                if (!fileType.startsWith('image/')) {
                    showToast("Error", "Please select an image file", "error");
                    resolve(false);
                    return;
                }

                // Size check for images (e.g., max 5MB)
                if (file.file.size > 5 * 1024 * 1024) {
                    showToast("Error", "Image size must be less than 5MB", "error");
                    resolve(false);
                    return;
                }

                // Validate image dimensions if needed
                const img = new Image();
                img.src = URL.createObjectURL(file.file);

                img.onload = () => {
                    URL.revokeObjectURL(img.src);
                    resolve(true);
                };

                img.onerror = () => {
                    URL.revokeObjectURL(img.src);
                    showToast("Error", "Invalid image file", "error");
                    resolve(false);
                };
            } else if (selectedMediaType === 'Video' || selectedMediaType === 'video') {
                // Allow only MP4, MOV, MKV, M4V
                const allowedVideoExtensions = ['mp4', 'mov', 'mkv', 'm4v'];
                const allowedVideoMimes = ['video/mp4', 'video/quicktime', 'video/x-matroska'];
                const fileExtension = file.name.split('.').pop()?.toLowerCase() || '';
                const isValidVideoMime = allowedVideoMimes.includes(fileType);
                const isValidVideoExt = allowedVideoExtensions.includes(fileExtension);
                if (!isValidVideoMime && !isValidVideoExt) {
                    showToast("Error", "Only MP4, MOV, MKV & M4V files are allowed", "error");
                    resolve(false);
                    return;
                }

                // Size check for videos (e.g., max 50MB)
                if (file.file.size > 50 * 1024 * 1024) {
                    showToast("Error", "Video size must be less than 50MB", "error");
                    resolve(false);
                    return;
                }

                resolve(true);
            } else {
                resolve(false);
            }
        });
    };

    const handleUpload = async (
        file: UploadedFile,
        onProgress?: (percent: number) => void
    ): Promise<{ key: string }> => {
        const parts = file.name?.split(".");
        const extension = parts.pop();

        try {
            const { data } = await mediaPreSignedUrl({
                fileName: file.name,
                fileType: extension,
            });

            if (!data?.url) {
                showToast("Error", "Could not generate upload URL", "error");
                return { key: '' };
            }

            return new Promise((resolve, reject) => {
                const xhr = new XMLHttpRequest();
                xhr.open(data.method, data.url, true);
                xhr.setRequestHeader("Content-Type", file.type);

                xhr.upload.onprogress = (event) => {
                    if (event.lengthComputable && onProgress) {
                        const percent = Math.round((event.loaded / event.total) * 100);
                        onProgress(percent);
                    }
                };

                xhr.onload = () => {
                    if (xhr.status === 200 || xhr.status === 204) {
                        resolve({ key: data.key });
                    } else {
                        reject(new Error("Upload failed"));
                    }
                };

                xhr.onerror = () => reject(new Error("Network error"));
                xhr.send(file.file);
            });
        } catch (err) {
            showToast("Error", "Upload preparation failed", "error");
            return { key: '' };
        }
    };


    return (
        <Container theme={theme}>
            <StyledText fontSize='13px' fontWeight={600} textColor={theme.CorporateTheme.colors.primary}>Content</StyledText>
            <Section>
                <StyledText fontSize='14px' fontWeight={600} textColor={theme.CorporateTheme.colors.headerColor}>Media Type</StyledText>
                <OptionsRow>
                    {headingOptions.map((heading) => (
                        <OptionButton
                            theme={theme}
                            key={heading.label}
                            $isSelected={selectedMediaType?.toLowerCase() === heading?.label.toLowerCase()}
                            onClick={() => {
                                // Switch to the new media type
                                updateTempSetting('mediaType', heading?.label);
                                // Don't reset media - preserve data for each type
                            }}
                        >
                            <div>
                                {heading?.icon}
                            </div>
                            <StyledText fontSize='14px' fontWeight={600} textColor={selectedMediaType?.toLowerCase() === heading?.label.toLowerCase() ? theme.CorporateTheme.colors.darkContrast : theme.CorporateTheme.colors.gunmetal}>{heading?.label}</StyledText>
                        </OptionButton>
                    ))}
                </OptionsRow>
            </Section>

            <div className='mt-4 pt-2'>
                <StyledHorizontalLine theme={theme} />
            </div>

            <Section theme={theme}>
                <StyledText fontSize='14px' fontWeight={600} textColor={theme.CorporateTheme.colors.headerColor}>
                    Upload {capitalizeFirstLetter(selectedMediaType)}
                </StyledText>

                {tempSettings[`${selectedMediaType.toLowerCase()}ExternalURL`] && (
                    <div style={{
                        background: hexToRgba(theme.CorporateTheme.colors.accentOrange, 0.1),
                        border: `1px solid ${theme.CorporateTheme.colors.accentOrange}`,
                        borderRadius: '4px',
                        padding: '8px 12px',
                        marginBottom: '10px'
                    }}>
                        <StyledText fontSize='12px' fontWeight={500} textColor={theme.CorporateTheme.colors.accentOrange}>
                            ℹ️ External source is active. Upload is disabled.
                        </StyledText>
                    </div>
                )}

                <Formik
                    initialValues={{
                        file: [],
                        mediaPreview: getMediaData('Preview') || '',
                    }}
                    enableReinitialize
                    onSubmit={(values) => {
                        console.log(values);
                    }}
                >
                    {({ values, setFieldValue }) => {
                        const handleFileChange = async (files: UploadedFile[]) => {
                            if (!files.length) return;
                            const file = files[0];

                            // Validate based on media type
                            const isValid = await validateMediaFile(file);
                            if (!isValid) {
                                setHasUploaded(false);
                                setUploadProgress(0);
                                setIsUploading(false);

                                // Reset everything for current media type
                                setFieldValue('file', []);
                                setFieldValue('mediaPreview', '');
                                setMediaData('Url', '');
                                setMediaData('Preview', '');

                                // Force reset
                                setUploadKey(prev => prev + 1);
                                return;
                            }

                            // Clear external URL when uploading a file
                            const externalUrlKey = `${selectedMediaType.toLowerCase()}ExternalURL`;
                            if (tempSettings[externalUrlKey]) {
                                updateTempSetting(externalUrlKey, '');
                            }

                            // Create preview
                            const reader = new FileReader();
                            reader.onload = () => {
                                if (reader.result) {
                                    setFieldValue('mediaPreview', reader.result);
                                    setMediaData('Preview', reader.result as string);
                                }
                            };
                            reader.readAsDataURL(file.file);

                            // Start upload
                            setIsUploading(true);
                            setUploadProgress(0);

                            handleUpload(file, setUploadProgress)
                                .then(({ key }) => {
                                    if (key) {
                                        setFieldValue('mediaUrl', key);
                                        setMediaData('Url', key);
                                        setMediaData('FileName', file.name);
                                        setMediaData('FileSize', file.file.size);
                                        setHasUploaded(true);
                                        showToast('Success', `${selectedMediaType} uploaded successfully`, 'success');
                                    }
                                })
                                .catch(() => {
                                    showToast('Error', `Failed to upload ${selectedMediaType.toLowerCase()}`, 'error');
                                })
                                .finally(() => {
                                    setIsUploading(false);
                                });
                        };

                        const handleRemoveMedia = () => {
                            setFieldValue('mediaUrl', '');
                            setFieldValue('mediaPreview', '');
                            setMediaData('Url', '');
                            setMediaData('Preview', '');
                            setMediaData('FileName', '');
                            setMediaData('FileSize', undefined);
                            setHasUploaded(false);
                        };

                        const hasUploadedMedia = getMediaData('Url') || getMediaData('Preview') || getMediaData('FileName');

                        return (
                            <div style={{
                                opacity: tempSettings[`${selectedMediaType.toLowerCase()}ExternalURL`] ? 0.5 : 1,
                                pointerEvents: tempSettings[`${selectedMediaType.toLowerCase()}ExternalURL`] ? 'none' : 'auto',
                                position: 'relative'
                            }}>
                                <FormikImageUpload
                                    key={uploadKey}
                                    name='file'
                                    accept={selectedMediaType === 'Image' || selectedMediaType === 'image' ? ".png, .jpg, .jpeg, .webp" : '.mp4,.mov,.mkv,.m4v,video/mp4,video/quicktime,video/x-matroska'}
                                    onChange={handleFileChange}
                                    setHasUploaded={setHasUploaded}
                                    previewUrl={values.mediaPreview}
                                    uploadProgress={uploadProgress}
                                    isUploading={isUploading}
                                    removeFile={handleRemoveMedia}
                                    existingFileName={getMediaData('FileName')}
                                    existingFileSize={getMediaData('FileSize')}
                                />
                            </div>
                        );
                    }}
                </Formik>
            </Section>

            <div className='mt-4 pt-2'>
                <StyledHorizontalLine theme={theme} />
            </div>

            <Section theme={theme}>
                <StyledText fontSize='13px' fontWeight={600} textColor={theme.CorporateTheme.colors.primary}>Media Alignment</StyledText>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 15 }}>
                    {textAlignmentOptions.map((alignment) => {
                        const value = alignment.id.toLowerCase();

                        return (
                            <AlignmentCard
                                key={value}
                                theme={theme}
                                $isSelected={tempSettings.textAlignment === value}
                                onClick={() => updateTempSetting('textAlignment', value)}
                            >
                                <StyledRadio borderWidth='2px' radioColor={theme.CorporateTheme.colors.primary} name="alignment" checked={tempSettings.textAlignment === value} onChange={() => { }} label="" />
                                {alignment.icon}
                            </AlignmentCard>
                        );
                    })}
                </div>
            </Section>


            <div className='mt-4 pt-2'>
                <StyledHorizontalLine theme={theme} />
            </div>

            <div className='col mt-3'>
                <StyledText fontSize='14px' fontWeight={600} textColor={theme.CorporateTheme.colors.headerColor} className='mb-2'>External Source</StyledText>

                {(getMediaData('Url') || getMediaData('Preview') || getMediaData('FileName')) && (
                    <div style={{
                        background: hexToRgba(theme.CorporateTheme.colors.primary, 0.1),
                        border: `1px solid ${theme.CorporateTheme.colors.primary}`,
                        borderRadius: '4px',
                        padding: '8px 12px',
                        marginBottom: '10px'
                    }}>
                        <StyledText fontSize='12px' fontWeight={500} textColor={theme.CorporateTheme.colors.primary}>
                            ℹ️ Uploaded media is active. External source is disabled.
                        </StyledText>
                    </div>
                )}

                <div style={{
                    opacity: (getMediaData('Url') || getMediaData('Preview') || getMediaData('FileName')) ? 0.5 : 1,
                    pointerEvents: (getMediaData('Url') || getMediaData('Preview') || getMediaData('FileName')) ? 'none' : 'auto'
                }}>
                    <CustomInputURL
                        value={tempSettings[`${selectedMediaType.toLowerCase()}ExternalURL`] || ''}
                        placeholder={selectedMediaType === 'Video' ? 'Paste video or YouTube link (e.g., youtube.com/watch?v=...' : `Paste image link here from online sources`}
                        validationType={selectedMediaType === 'Video' ? 'video' : 'image'}
                        validateOnChange={true}
                        onChange={(value) => {
                            const externalUrlKey = `${selectedMediaType.toLowerCase()}ExternalURL`;
                            updateTempSetting(externalUrlKey, value);
                            // Clear uploaded media when external URL is set
                            if (value) {
                                setMediaData('Url', '');
                                setMediaData('Preview', '');
                                setMediaData('FileName', '');
                                setMediaData('FileSize', undefined);
                                setUploadKey(prev => prev + 1);
                                setHasUploaded(false);
                            }
                        }}
                    />
                </div>
            </div>
            {selectedMediaType === 'Video' ? null :
                <div className='mt-4 pt-2'>
                    <StyledHorizontalLine theme={theme} />
                </div>}

            {selectedMediaType === 'Video' ? null : <div className='col mt-3'>
                <div className='d-flex align-items-center gap-1 mb-2'><StyledText fontSize='14px' fontWeight={600} textColor={theme.CorporateTheme.colors.headerColor}>Interactivity</StyledText>
                    <StyledText fontSize='14px' fontWeight={400} textColor={theme.CorporateTheme.colors.primary}>(Link)</StyledText></div>
                <CustomInputURL
                    value={tempSettings.linkURL || ''}
                    placeholder='Paste destination link here'
                    validationType="generic"
                    validateOnChange={true}
                    onChange={(value) => {
                        updateTempSetting('linkURL', value);
                    }}
                />
            </div>}

            {selectedMediaType === 'Video' ? null : <div className='mt-3'>
                <StyledCheckBox
                    className="cursor-pointer d-flex align-items-center justify-content-center rounded-1"
                    size={16}
                    borderColor={
                        tempSettings.openNewTab
                            ? theme.CorporateTheme?.colors?.primary
                            : theme.CorporateTheme?.colors?.headerColor
                    }
                    bgColor={theme.CorporateTheme?.colors?.primary}
                    checkColor={theme.colors.contentBackground}
                    value={tempSettings.openNewTab}
                    onChange={() => updateTempSetting('openNewTab', !tempSettings.openNewTab)}
                    label="Open in new tab?"
                />
            </div>}

        </Container>
    )
}

const Container = styled.div<{ theme: Theme }>`
    display: flex;
    flex-direction: column; 
    border-radius: 8px;
    padding: 16px;
    background-color: ${({ theme }) => theme.CorporateTheme.colors.background};
    border: 1px solid ${({ theme }) => theme.CorporateTheme.colors.border};
    `

const Section = styled.div<{ theme: Theme }>`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 26px;
`;


const OptionsRow = styled.div`
    display: flex;
    flex-wrap: wrap;
`;

const OptionButton = styled.button<{ theme: Theme, $isSelected?: boolean }>`
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap:10px;
    padding: 8px 12px;
    border: 1px solid ${({ theme, $isSelected }) =>
        $isSelected ? theme.CorporateTheme.colors.accentOrange : theme.CorporateTheme.colors.border};
    background-color: ${({ theme, $isSelected }) =>
        $isSelected ? hexToRgba(theme.CorporateTheme.colors.accentOrange, 0.1) : theme.CorporateTheme.colors.background};
    color: ${({ theme, $isSelected }) =>
        $isSelected ? theme.CorporateTheme.colors.accentOrange : theme.CorporateTheme.colors.textPrimary};
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    text-align: center;
    height:72px;
    
    /* Remove margin */
    margin: 0;
    
    /* Default no border-radius */
    border-radius: 0;
    
    /* First button - rounded left corners */
    &:first-child {
        border-top-left-radius: 8px;
        border-bottom-left-radius: 8px;
    }

    /* Last button - rounded right corners */
    &:last-child {
        border-top-right-radius: 8px;
        border-bottom-right-radius: 8px;
    }

    /* Remove double borders between buttons */
    &:not(:first-child) {
        margin-left: -1px;
    }

    /* IMPORTANT: Set z-index for all buttons to prevent overlap issues */
    position: relative;
    z-index: ${({ $isSelected }) => $isSelected ? 2 : 1};

    &:hover {
        background-color: ${({ theme }) => hexToRgba(theme.CorporateTheme.colors.accentOrange, 0.1)};
        border-color: ${({ theme }) => theme.CorporateTheme.colors.accentOrange};
        color: ${({ theme }) => theme.CorporateTheme.colors.accentOrange};
        z-index: 3; /* Higher z-index on hover */
    }

    /* Selected state styling */
    ${({ $isSelected, theme }) => $isSelected && `
        background-color: ${hexToRgba(theme.CorporateTheme.colors.accentOrange, 0.1)};
        border-color: ${theme.CorporateTheme.colors.accentOrange};
        color: ${theme.CorporateTheme.colors.accentOrange};
        font-weight: 600;
        z-index: 2;
    `}
`;

const TextInput = styled.input<{ theme: Theme }>`
    padding: 10px 12px;
    border-radius: 4px;
    border: 1px solid ${({ theme }) => theme.CorporateTheme.colors.border};
    background-color: ${({ theme }) => theme.CorporateTheme.colors.background};
    color: ${({ theme }) => theme.CorporateTheme.colors.textPrimary};
    font-size: 14px;
    
    &:focus {
        outline: none;
        border-color: ${({ theme }) => theme.CorporateTheme.colors.primary};
    }
    
    &::placeholder {
        color: ${({ theme }) => theme.CorporateTheme.colors.textSecondary};
    }
`;

const Select = styled.select<{ theme: Theme }>`
    padding: 10px 12px;
    border-radius: 4px;
    border: 1px solid ${({ theme }) => theme.CorporateTheme.colors.border};
    background-color: ${({ theme }) => theme.CorporateTheme.colors.headerBG};
    color: ${({ theme }) => theme.CorporateTheme.colors.textPrimary};
    font-size: 14px;
    
    &:focus {
        outline: none;
        border-color: ${({ theme }) => theme.CorporateTheme.colors.primary};
    }
    
    option {
        background-color: ${({ theme }) => theme.CorporateTheme.colors.headerBG};
        color: ${({ theme }) => theme.CorporateTheme.colors.textPrimary};
    }
`;


const AlignmentCard = styled.button<{
    theme: any;
    $isSelected: boolean;
}>`
  display: flex;
  align-items: center;
    border:none;
  cursor: pointer;
    gap: 6px;
    background: ${({ theme }) => theme.CorporateTheme.colors.background};

  transition: all 0.2s ease;


`;



export default MediaProperties
